import { useMemo } from "react";
import { DataTable, Dialog, FormLabel, Heading, Loader, Select } from "@/components";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Pencil, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MasterModuleLayoutProps } from "./types";
import { Badge, Button, Input, Label, Switch } from "@/components/ui";

const MasterModuleLayout = ({ title, hookResult, extraColumns = [] }: MasterModuleLayoutProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    onAdd,
    globalFilter,
    setGlobalFilter,
    moduleData,
    isDataLoading,
    isOwner,
    hasAddPermission,
    hasUpdatePermission,
    communityList,
    communityId,
    communityKey,
    onCommunityChange,
    isCommunityLoading,
    toggleStatus,
    isUpdateStatusPending,
    idKey,
    nameKey,
    module,
    isDeleteFn,
    selectedRow,
    setOpenDeleteDialog,
    openDeleteDialog,
    hideDesc,
    isStatusUpdateApi,
    hideActiveSwitch,
    onRowClick,
  } = hookResult;

  const onDeleteConfirm = () => {
    toggleStatus({
      id: selectedRow.current?.[idKey],
      module,
      communityId: selectedRow.current?.communityId,
    });
  }

  const isDeletedRow = (row: any) =>
    row?.deleted || row?.isDeleted || row?.delete || row?.deletedAt || row?.active === false;

  // Standardized Columns Generator
  const columns = useMemo(() => {
    const baseColumns = [
      {
        // Show the per-community display id (starts at 1 in every community) where the module has one
        accessorKey: idKey,
        header: "ID",
        accessorFn: (row: any) => String(row?.displayId ?? row[idKey] ?? ""),
      },
      {
        accessorKey: nameKey,
        header: "Name",
        accessorFn: (row: any) => String(row[nameKey] || ""),
      },
      ...(hideDesc ? [] : [{
        accessorKey: "description",
        header: "Description",
        accessorFn: (row: any) => String(row?.description || ""),
      }]),
      ...extraColumns,
      ...(isDeleteFn ? [{
        accessorKey: "active",
        header: "Status",
        cell: ({ row }: any) => {
          const isDeleted = isDeletedRow(row?.original);
          return (
            <Badge
              variant="outline"
              className={cn(
                isDeleted ? "text-red-600 border-red-600 bg-red-50" : "text-green-600 border-green-600 bg-green-50",
                "rounded-full px-4 py-2"
              )}
            >
              {isDeleted ? "Inactive" : "Active"}
            </Badge>
          );
        },
      }] : [{
        accessorKey: "active",
        header: "Status",
        cell: ({ row }: any) => {
          const isActive = row?.original?.active;
          return (
            <Badge
              variant="outline"
              className={cn(
                isActive ? "text-green-600 border-green-600 bg-green-50" : "text-red-600 border-red-600 bg-red-50",
                "rounded-full px-4 py-2"
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      }]),
    ];

    if (!isOwner && hasUpdatePermission) {
      baseColumns.push({
        id: "actions",
        header: "Actions",
        cell: ({ row }: any) => {
          const isDeleted = isDeleteFn && isDeletedRow(row?.original);

          return (
            <DropdownMenu>
            <DropdownMenuTrigger disabled={isDeleted}>
              <MoreVertical className={cn("w-5 h-5", isDeleted ? "cursor-not-allowed opacity-50" : "cursor-pointer")} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-0 shadow-md">
              <DropdownMenuItem
                className="flex items-center gap-2"
                disabled={isDeleted}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isDeleted) return;
                  navigate(`${row.original?.[idKey]}/edit`);
                }}
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              {isDeleteFn ?
                <DropdownMenuItem
                  className="flex items-center gap-2"
                  disabled={isDeleted}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isDeleted) return;
                    selectedRow.current = row.original;
                    setOpenDeleteDialog(true);
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenuItem>
               : hideActiveSwitch ? <></> :
                <DropdownMenuLabel className="flex items-center gap-2">
                  <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      id={`active-${row.original[idKey]}`}
                      onCheckedChange={(checked: boolean) => {
                        toggleStatus({
                          id: row.original[idKey],
                          module,
                          communityId: row.original.communityId,
                          ...(isStatusUpdateApi ? {data: {
                            active: checked
                          }} : {})
                        });
                      }}
                      checked={row.original.active}
                      disabled={isUpdateStatusPending}
                    />
                    <Label htmlFor={`active-${row.original[idKey]}`}>
                      {row.original.active ? "Active" : "Inactive"}
                    </Label>
                  </div>
                </DropdownMenuLabel>
              }
            </DropdownMenuContent>
          </DropdownMenu>
        )},
      });
    }

    return baseColumns;
  }, [idKey, nameKey, extraColumns, isOwner, hasUpdatePermission, isUpdateStatusPending, isDeleteFn, module]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title={title} />
          {!isOwner && isMobile && hasAddPermission && (
            <Button onClick={onAdd} className="px-2">
              + Add {title}
            </Button>
          )}
        </div>
        <div className="flex flex-row items-center gap-4">
          {isOwner ? <></> : <div className="relative w-full sm:w-72 sm:min-w-72 sm:max-w-72 shrink-0">
            <FormLabel>Select Community</FormLabel>
            <Select
              key={communityKey}
              placeholder={""}
              searchPlaceholder={""}
              noDataPlaceholder="Community Not Found"
              options={communityList?.map((data: any) => ({
                value: data?.communityId?.toString(),
                label: data?.name,
              }))}
              value={communityId?.toString()}
              onChange={onCommunityChange}
              className="h-[36px] w-full"
              disabled={isCommunityLoading}
            />
          </div>}
          <Input
            type="text"
            placeholder="Search"
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full sm:w-72 bg-white h-[40px]"
            startIcon={<Search className="w-4 h-4" />}
          />
          {!isOwner && !isMobile && hasAddPermission && (
            <Button onClick={onAdd}>+ Add {title}</Button>
          )}
        </div>
      </div>

      {isDataLoading || isCommunityLoading ? (
        <Loader />
      ) : (
        <DataTable
          columns={columns}
          data={
            moduleData?.data?.sort(
              (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            ) || []
          }
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
          onRowClick={onRowClick}
        />
      )}
      <Dialog
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        title={
          <div className="text-2xl font-bold text-primary">
            {"Confirmation"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
        saveText="Delete"
        saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
        isLoading={isUpdateStatusPending}
        onSave={onDeleteConfirm}
      >
        <p className="text-sm">
          Are you sure you want to delete this? This action cannot be undone
        </p>
      </Dialog>
    </div>
  );
};

export default MasterModuleLayout
